import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, ListSubheader, Select, TextField } from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

const SearchableSelect = ({
  children,
  searchPlaceholder = "Search",
  getSearchText,
  MenuProps = {},
  ...selectProps
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const extractTextFromMenuItem = (menuItem) => {
    if (!menuItem?.props) return "";

    if (menuItem.props.disabled && menuItem.props.value === "") {
      return "";
    }

    const children = menuItem.props.children;

    if (typeof children === "string") {
      return children;
    }
    if (React.isValidElement(children) && typeof children?.props?.children === "string") {
      return children.props.children;
    }

    return String(children || "");
  };

  const filteredChildren = useMemo(() => {
    if (!searchQuery.trim()) {
      return React.Children.toArray(children);
    }

    const query = searchQuery.toLowerCase();

    return React.Children.toArray(children).filter((child) => {
      if (child.props?.disabled && child.props?.value === "") {
        return true;
      }

      if (getSearchText) {
        const searchText = getSearchText(child);
        return searchText.toLowerCase().includes(query);
      }

      const text = extractTextFromMenuItem(child);
      return text.toLowerCase().includes(query);
    });
  }, [children, searchQuery, getSearchText]);

  const enhancedMenuProps = {
    ...MenuProps,
    disableAutoFocusItem: true,
    PaperProps: {
      sx: { maxHeight: 420, minWidth: 320 },
      ...MenuProps.PaperProps,
    },
    MenuListProps: {
      ...MenuProps.MenuListProps,
      subheader: (
        <ListSubheader disableSticky sx={{ bgcolor: "background.paper", px: 1.5, pt: 1.25, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            onKeyDown={(e) => e.stopPropagation()}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </ListSubheader>
      ),
    },
  };

  return (
    <Select {...selectProps} MenuProps={enhancedMenuProps}>
      {filteredChildren}
    </Select>
  );
};

SearchableSelect.propTypes = {
  children: PropTypes.node.isRequired,
  searchPlaceholder: PropTypes.string,
  getSearchText: PropTypes.func,
  MenuProps: PropTypes.object,
};

export default SearchableSelect;
